import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import PCMAnalysisChart from './PCMAnalysisChart';
import { 
  LogOut, 
  Download, 
  FileText, 
  Users, 
  Calendar, 
  Search,
  Filter,
  RefreshCw,
  Eye,
  BarChart3,
  TrendingUp,
  Trash2,
  CheckSquare,
  Square,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

const AdminDashboard = ({ token, onLogout, language }) => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ calendar_url: '', calendar_duration: 15 });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Types de diagnostic friction
  const frictionDimensions = {
    clarity: { fr: 'Clarté des priorités', en: 'Clarity of priorities' },
    decision: { fr: 'Efficacité décisionnelle', en: 'Decision efficiency' },
    accountability: { fr: 'Responsabilisation', en: 'Accountability' },
    communication: { fr: 'Communication', en: 'Communication' },
    execution: { fr: 'Exécution', en: 'Execution' },
    leader: { fr: 'Exécution', en: 'Execution' }
  };

  const frictionColors = {
    clarity: '#007BFF',
    decision: '#8E44AD',
    accountability: '#FF8C00',
    communication: '#52a385',
    execution: '#e62c86',
    leader: '#e62c86'
  };

  // Anciens types PCM (pour compatibilité avec anciennes données)
  const typeNames = {
    analytical: { fr: 'Manager Analytique', en: 'Analytical Manager' },
    principled: { fr: 'Manager de Conviction', en: 'Persister Manager' },
    collaborative: { fr: 'Manager Collaboratif', en: 'Harmonizer Manager' },
    creative: { fr: 'Manager Innovant', en: 'Energizer Manager' },
    visionary: { fr: 'Manager Stratégique', en: 'Imaginer Manager' },
    dynamic: { fr: 'Manager d\'Action', en: 'Promoter Manager' }
  };

  // Couleurs PCM (pour compatibilité)
  const pcmColors = {
    analytical: '#007BFF',
    principled: '#8E44AD',
    collaborative: '#FF8C00',
    creative: '#FFD700',
    visionary: '#8B4513',
    dynamic: '#DC143C'
  };

  // Fonction pour obtenir le badge d'affichage selon le type de diagnostic
  const getAssessmentBadge = (assessment) => {
    if (assessment.diagnostic_type === 'friction' || assessment.friction_index !== undefined) {
      const frictionIndex = assessment.friction_index || 0;
      let color = '#52a385'; // Fluid
      let label = language === 'fr' ? 'Fluide' : 'Fluid';
      
      if (frictionIndex >= 75) {
        color = '#DC143C';
        label = language === 'fr' ? 'Friction élevée' : 'High friction';
      } else if (frictionIndex >= 50) {
        color = '#FF8C00';
        label = language === 'fr' ? 'Friction structurelle' : 'Structural friction';
      } else if (frictionIndex >= 25) {
        color = '#f3a114';
        label = language === 'fr' ? 'Tensions latentes' : 'Latent tensions';
      }
      
      return { color, label: `${Math.round(frictionIndex)}/100 - ${label}` };
    }
    
    // PCM legacy
    const type = assessment.dominant_type;
    return {
      color: pcmColors[type] || '#9dbc1d',
      label: typeNames[type]?.[language] || type
    };
  };

  useEffect(() => {
    loadAssessments(currentPage);
    loadSettings();
  }, [currentPage, itemsPerPage]);

  const loadAssessments = async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/recent?page=${page}&limit=${itemsPerPage}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssessments(data.assessments || []);
        setTotalPages(data.total_pages || 1);
        setTotalCount(data.total || 0);
        setCurrentPage(data.page || 1);
      } else {
        console.error('Erreur lors du chargement des évaluations');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setSelectedItems([]); // Reset selection when changing page
    }
  };

  const exportToPDF = async (assessmentId, format = 'technical') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/${assessmentId}/export?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        const formatName = format === 'coaching' ? 'coaching' : 'technique';
        a.download = `rapport_${formatName}_${assessmentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        alert(language === 'fr' ? 'Erreur lors de l\'export PDF' : 'Error during PDF export');
      }
    } catch (error) {
      console.error('Erreur export PDF:', error);
      alert(language === 'fr' ? 'Erreur lors de l\'export PDF' : 'Error during PDF export');
    }
  };

  const exportTechnicalPDF = (assessmentId) => {
    exportToPDF(assessmentId, 'technical');
  };

  const exportCoachingPDF = (assessmentId) => {
    exportToPDF(assessmentId, 'coaching');
  };

  // Fonctions pour gérer les paramètres
  const loadSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
      const data = await response.json();
      if (data.success) {
        setSettings({
          calendar_url: data.settings.calendar_url || '',
          calendar_duration: data.settings.calendar_duration || 15
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          calendar_url: settings.calendar_url,
          calendar_duration: settings.calendar_duration
        })
      });
      
      const data = await response.json();
      if (data.success) {
        alert(language === 'fr' ? 'Paramètres sauvegardés avec succès' : 'Settings saved successfully');
        setShowSettings(false);
      } else {
        alert(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving settings');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const exportAllToPDF = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/export-all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `all_assessments_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erreur export global:', error);
      alert(language === 'fr' ? 'Erreur lors de l\'export global' : 'Error during global export');
    }
  };

  const filteredAssessments = assessments.filter(assessment => {
    const matchesSearch = assessment.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assessment.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || assessment.dominant_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    onLogout();
  };

  // Gestion de la sélection
  const handleSelectItem = (assessmentId) => {
    setSelectedItems(prev => 
      prev.includes(assessmentId) 
        ? prev.filter(id => id !== assessmentId)
        : [...prev, assessmentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredAssessments.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredAssessments.map(a => a.id));
    }
  };

  // Suppression individuelle
  const handleDeleteSingle = async (assessmentId, userName) => {
    setDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/${assessmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setAssessments(prev => prev.filter(a => a.id !== assessmentId));
        setShowDeleteConfirm(null);
        alert(language === 'fr' ? `Évaluation de ${userName} supprimée avec succès` : `Assessment for ${userName} deleted successfully`);
      } else {
        alert(language === 'fr' ? 'Erreur lors de la suppression' : 'Error during deletion');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert(language === 'fr' ? 'Erreur lors de la suppression' : 'Error during deletion');
    } finally {
      setDeleting(false);
    }
  };

  // Suppression par lot
  const handleDeleteBulk = async () => {
    if (selectedItems.length === 0) return;
    
    if (!window.confirm(language === 'fr' ? 
      `Êtes-vous sûr de vouloir supprimer ${selectedItems.length} évaluation(s) ?` :
      `Are you sure you want to delete ${selectedItems.length} assessment(s)?`
    )) return;

    setDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/bulk`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assessment_ids: selectedItems })
      });
      
      if (response.ok) {
        const result = await response.json();
        setAssessments(prev => prev.filter(a => !selectedItems.includes(a.id)));
        setSelectedItems([]);
        alert(language === 'fr' ? 
          `${result.deleted_count} évaluation(s) supprimée(s) avec succès` :
          `${result.deleted_count} assessment(s) deleted successfully`
        );
      } else {
        alert(language === 'fr' ? 'Erreur lors de la suppression' : 'Error during deletion');
      }
    } catch (error) {
      console.error('Erreur suppression groupée:', error);
      alert(language === 'fr' ? 'Erreur lors de la suppression' : 'Error during deletion');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1c47] p-4">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {language === 'fr' ? 'Administration WEDO' : 'WEDO Administration'}
            </h1>
            <p className="text-gray-300">
              {language === 'fr' ? 'Gestion des évaluations de leadership' : 'Leadership assessments management'}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => setShowSettings(true)}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <Settings className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Paramètres' : 'Settings'}
            </Button>
            
            <Button
              onClick={exportAllToPDF}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <FileText className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Export Global PDF' : 'Export All PDF'}
            </Button>
            
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-red-500/20 border-red-500/50 text-red-200 hover:bg-red-500/30"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Déconnexion' : 'Logout'}
            </Button>
          </div>
        </div>

        {/* Enhanced Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white/95 backdrop-blur-sm border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">
                    {language === 'fr' ? 'Total Évaluations' : 'Total Assessments'}
                  </p>
                  <p className="text-3xl font-bold text-gray-800">{assessments.length}</p>
                </div>
                <Users className="w-8 h-8 text-[#e62c86]" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/95 backdrop-blur-sm border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">
                    {language === 'fr' ? 'Aujourd\'hui' : 'Today'}
                  </p>
                  <p className="text-3xl font-bold text-gray-800">
                    {assessments.filter(a => new Date(a.date).toDateString() === new Date().toDateString()).length}
                  </p>
                </div>
                <Calendar className="w-8 h-8 text-[#52a385]" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/95 backdrop-blur-sm border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">
                    {language === 'fr' ? 'Friction Moyenne' : 'Avg Friction'}
                  </p>
                  <p className="text-lg font-bold text-gray-800">
                    {(() => {
                      const frictionAssessments = assessments.filter(a => a.friction_index !== undefined);
                      if (frictionAssessments.length === 0) {
                        // Fallback pour anciens diagnostics PCM
                        const types = assessments.map(a => a.dominant_type).filter(Boolean);
                        const mostCommon = types.sort((a,b) =>
                          types.filter(v => v === a).length - types.filter(v => v === b).length
                        ).pop();
                        return typeNames[mostCommon]?.[language] || mostCommon || 'N/A';
                      }
                      const avgFriction = frictionAssessments.reduce((sum, a) => sum + (a.friction_index || 0), 0) / frictionAssessments.length;
                      return `${Math.round(avgFriction)}/100`;
                    })()}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-[#f3a114]" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/95 backdrop-blur-sm border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">
                    {language === 'fr' ? 'Diagnostics Friction' : 'Friction Diagnostics'}
                  </p>
                  <p className="text-3xl font-bold text-gray-800">
                    {assessments.filter(a => a.friction_index !== undefined || a.diagnostic_type === 'friction').length}
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-[#74388e]" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* PCM Distribution Chart */}
        {assessments.length > 0 && (
          <Card className="bg-white/95 backdrop-blur-sm border-0 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#e62c86]" />
                {language === 'fr' ? 'Distribution des Profils PCM' : 'PCM Profile Distribution'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(typeNames).map(([typeKey, names]) => {
                  const count = assessments.filter(a => a.dominant_type === typeKey).length;
                  const percentage = assessments.length > 0 ? (count / assessments.length) * 100 : 0;
                  
                  return (
                    <div key={typeKey} className="flex items-center gap-4">
                      <div className="w-32 text-sm font-medium text-gray-700">
                        {names[language]}
                      </div>
                      <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                        <div 
                          className="h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ 
                            width: `${Math.max(percentage, 5)}%`,
                            background: pcmColors[typeKey] || '#9dbc1d'
                          }}
                        >
                          {count > 0 && (
                            <span className="text-white text-xs font-bold">
                              {count}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-16 text-right text-sm font-medium text-gray-600">
                        {percentage.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="bg-white/95 backdrop-blur-sm border-0 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex gap-4 items-center flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder={language === 'fr' ? 'Rechercher par nom ou email...' : 'Search by name or email...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 bg-white"
                >
                  <option value="all">
                    {language === 'fr' ? 'Tous les types' : 'All types'}
                  </option>
                  {Object.entries(typeNames).map(([key, names]) => (
                    <option key={key} value={key}>
                      {names[language]}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-2">
                {selectedItems.length > 0 && (
                  <Button
                    onClick={handleDeleteBulk}
                    disabled={deleting}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {language === 'fr' ? `Supprimer (${selectedItems.length})` : `Delete (${selectedItems.length})`}
                  </Button>
                )}
                
                <Button onClick={() => loadAssessments(currentPage)} variant="outline" size="sm">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Actualiser' : 'Refresh'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card className="bg-white/95 backdrop-blur-sm border-0">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {language === 'fr' ? 'Évaluations Récentes' : 'Recent Assessments'} 
                <Badge variant="secondary">{totalCount}</Badge>
                {totalCount > 0 && (
                  <span className="text-sm font-normal text-gray-500">
                    ({language === 'fr' ? `Page ${currentPage}/${totalPages}` : `Page ${currentPage}/${totalPages}`})
                  </span>
                )}
              </div>
              
              {filteredAssessments.length > 0 && (
                <Button
                  onClick={handleSelectAll}
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
                >
                  {selectedItems.length === filteredAssessments.length ? 
                    <CheckSquare className="w-4 h-4" /> : 
                    <Square className="w-4 h-4" />
                  }
                  {language === 'fr' ? 'Tout sélectionner' : 'Select all'}
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">
                  {language === 'fr' ? 'Chargement...' : 'Loading...'}
                </p>
              </div>
            ) : filteredAssessments.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">
                  {language === 'fr' ? 'Aucune évaluation trouvée' : 'No assessments found'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAssessments.map((assessment) => (
                  <div key={assessment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      
                      {/* Checkbox + User Info */}
                      <div className="flex items-start gap-3 flex-1">
                        {/* Checkbox */}
                        <Button
                          onClick={() => handleSelectItem(assessment.id)}
                          variant="ghost"
                          size="sm"
                          className="mt-1 p-1 h-6 w-6"
                        >
                          {selectedItems.includes(assessment.id) ? 
                            <CheckSquare className="w-4 h-4 text-[#e62c86]" /> : 
                            <Square className="w-4 h-4 text-gray-400" />
                          }
                        </Button>
                        
                        {/* User Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-gray-800">{assessment.user}</h3>
                            {(() => {
                              const badge = getAssessmentBadge(assessment);
                              return (
                                <Badge 
                                  variant="secondary" 
                                  style={{
                                    backgroundColor: badge.color,
                                    color: '#ffffff'
                                  }}
                                  className="border-0"
                                >
                                  {badge.label}
                                </Badge>
                              );
                            })()}
                            {assessment.diagnostic_type === 'friction' && (
                              <Badge variant="outline" className="text-xs border-[#52a385] text-[#52a385]">
                                Friction
                              </Badge>
                            )}
                          </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                          <div>📧 {assessment.email}</div>
                          <div>📱 {assessment.phone || 'N/A'}</div>
                          <div>📅 {new Date(assessment.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</div>
                        </div>
                        
                        {/* Scores Summary */}
                          <div className="mt-3 flex flex-wrap gap-2">
                            {Object.entries(assessment.scores).map(([type, score]) => {
                              // Handle both friction (object) and PCM (number) score formats
                              const displayScore = typeof score === 'object' 
                                ? `${Math.round(100 - (score.frictionScore || 0))}%` 
                                : `${score}/30`;
                              const dimensionName = assessment.diagnostic_type === 'friction' || assessment.friction_index !== undefined
                                ? (frictionDimensions[type]?.[language] || type)
                                : (typeNames[type]?.[language] || type);
                              const dimensionColor = assessment.diagnostic_type === 'friction' || assessment.friction_index !== undefined
                                ? frictionColors[type]
                                : pcmColors[type];
                              
                              return (
                                <span 
                                  key={type} 
                                  className="px-2 py-1 rounded text-xs text-white"
                                  style={{ backgroundColor: dimensionColor || '#9dbc1d' }}
                                >
                                  {dimensionName}: {displayScore}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setSelectedAssessment(assessment)}
                          variant="outline"
                          size="sm"
                          className="text-[#52a385] border-[#52a385] hover:bg-[#52a385]/10"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {language === 'fr' ? 'Voir' : 'View'}
                        </Button>
                        
                        <Button
                          onClick={() => exportTechnicalPDF(assessment.id)}
                          variant="outline"
                          size="sm"
                          className="text-[#e62c86] border-[#e62c86] hover:bg-[#e62c86]/10"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {language === 'fr' ? 'PDF Technique' : 'Technical PDF'}
                        </Button>
                        
                        <Button
                          onClick={() => exportCoachingPDF(assessment.id)}
                          variant="outline"
                          size="sm"
                          className="text-[#52a385] border-[#52a385] hover:bg-[#52a385]/10"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {language === 'fr' ? 'PDF Coaching' : 'Coaching PDF'}
                        </Button>
                        
                        <Button
                          onClick={() => setShowDeleteConfirm({id: assessment.id, name: assessment.user})}
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-6 border-t mt-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>
                        {language === 'fr' 
                          ? `Affichage de ${(currentPage - 1) * itemsPerPage + 1} à ${Math.min(currentPage * itemsPerPage, totalCount)} sur ${totalCount} résultats`
                          : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(currentPage * itemsPerPage, totalCount)} of ${totalCount} results`
                        }
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {/* First Page */}
                      <Button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                        className="px-2"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </Button>
                      
                      {/* Previous Page */}
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                        className="px-2"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center gap-1 mx-2">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              className={`w-8 h-8 p-0 ${currentPage === pageNum ? 'bg-[#e62c86] hover:bg-[#c5246f]' : ''}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      {/* Next Page */}
                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        size="sm"
                        className="px-2"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      
                      {/* Last Page */}
                      <Button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        size="sm"
                        className="px-2"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Items per page selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        {language === 'fr' ? 'Par page:' : 'Per page:'}
                      </span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Enhanced Assessment Detail Modal */}
      {selectedAssessment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedAssessment(null)}>
          <Card className="max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="sticky top-0 bg-white z-10 border-b">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-6 h-6 text-[#e62c86]" />
                  <span className="text-xl">{selectedAssessment.user}</span>
                  {(() => {
                    const badge = getAssessmentBadge(selectedAssessment);
                    return (
                      <Badge 
                        variant="secondary" 
                        style={{ backgroundColor: badge.color, color: '#ffffff' }}
                        className="border-0"
                      >
                        {badge.label}
                      </Badge>
                    );
                  })()}
                </div>
                <Button onClick={() => setSelectedAssessment(null)} variant="ghost" size="sm">
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-6">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Colonne 1: Informations personnelles */}
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#52a385]" />
                      {language === 'fr' ? 'Informations personnelles' : 'Personal Information'}
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Nom:</span>
                        <span>{selectedAssessment.user}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Email:</span>
                        <span className="text-right break-all">{selectedAssessment.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">{language === 'fr' ? 'Téléphone' : 'Phone'}:</span>
                        <span>{selectedAssessment.phone || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">{language === 'fr' ? 'Date' : 'Date'}:</span>
                        <span>{new Date(selectedAssessment.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Scores Overview */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#f3a114]" />
                      {language === 'fr' ? 'Scores Rapides' : 'Quick Scores'}
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(selectedAssessment.scores)
                        .sort(([,a], [,b]) => {
                          const scoreA = typeof a === 'object' ? (100 - (a.frictionScore || 0)) : a;
                          const scoreB = typeof b === 'object' ? (100 - (b.frictionScore || 0)) : b;
                          return scoreB - scoreA;
                        })
                        .map(([type, score]) => {
                          const isFriction = selectedAssessment.diagnostic_type === 'friction' || selectedAssessment.friction_index !== undefined && selectedAssessment.friction_index !== null;
                          const numericScore = typeof score === 'object' ? (100 - (score.frictionScore || 0)) : score;
                          const maxScore = isFriction ? 100 : 30;
                          const displayScore = isFriction ? `${Math.round(numericScore)}%` : `${score}/30`;
                          const dimensionName = isFriction 
                            ? (frictionDimensions[type]?.[language] || type)
                            : (typeNames[type]?.[language] || type);
                          const dimensionColor = isFriction ? frictionColors[type] : pcmColors[type];
                          
                          return (
                            <div key={type} className="flex items-center justify-between text-sm">
                              <span className="font-medium text-gray-600" style={{ color: dimensionColor }}>
                                {dimensionName}
                              </span>
                              <div className="flex items-center gap-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="h-2 rounded-full transition-all"
                                    style={{ 
                                      width: `${(numericScore/maxScore)*100}%`,
                                      backgroundColor: dimensionColor || '#52a385'
                                    }}
                                  ></div>
                                </div>
                                <span className="font-bold text-gray-800 w-12 text-right">
                                  {displayScore}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Raw Answers */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-3">
                      {language === 'fr' ? 'Réponses (Brutes)' : 'Raw Answers'}
                    </h4>
                    <div className="text-xs text-gray-600 space-y-1">
                      {Object.entries(selectedAssessment.answers || {}).map(([questionIndex, answerIndex]) => (
                        <span key={questionIndex} className="inline-block mr-2 mb-1 bg-white px-2 py-1 rounded">
                          Q{parseInt(questionIndex) + 1}: R{typeof answerIndex === 'number' ? answerIndex : parseInt(answerIndex) + 1}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Colonnes 2 & 3: Analyse selon le type */}
                <div className="lg:col-span-2">
                  <div className="bg-gradient-to-br from-[#0f1c47]/5 to-[#e62c86]/5 p-1 rounded-lg">
                    <div className="bg-white rounded-lg p-4">
                      {(selectedAssessment.diagnostic_type === 'friction' || (selectedAssessment.friction_index !== undefined && selectedAssessment.friction_index !== null)) ? (
                        <>
                          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <BarChart3 className="w-6 h-6 text-[#e62c86]" />
                            {language === 'fr' ? 'Analyse Friction' : 'Friction Analysis'}
                          </h4>
                          <div className="text-center mb-6">
                            <p className="text-gray-500 text-sm mb-2">{language === 'fr' ? 'Indice de friction' : 'Friction Index'}</p>
                            <p className="text-5xl font-bold" style={{ color: getAssessmentBadge(selectedAssessment).color }}>
                              {Math.round(selectedAssessment.friction_index || 0)}
                            </p>
                            <p className="text-gray-400 text-sm">/ 100</p>
                          </div>
                          <div className="space-y-3">
                            {Object.entries(selectedAssessment.scores || {}).map(([dim, score]) => {
                              const frictionScore = typeof score === 'object' ? (score.frictionScore || 0) : 0;
                              const fluidityScore = 100 - frictionScore;
                              return (
                                <div key={dim}>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="font-medium" style={{ color: frictionColors[dim] }}>
                                      {frictionDimensions[dim]?.[language] || dim}
                                    </span>
                                    <span className="text-gray-600">{Math.round(fluidityScore)}% fluidité</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div 
                                      className="h-3 rounded-full transition-all"
                                      style={{ width: `${fluidityScore}%`, backgroundColor: frictionColors[dim] || '#52a385' }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {selectedAssessment.selected_consequence && (
                            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                              <p className="text-sm font-medium text-gray-600 mb-1">{language === 'fr' ? 'Auto-positionnement :' : 'Self-positioning:'}</p>
                              <p className="text-sm text-gray-800">{selectedAssessment.selected_consequence}</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <BarChart3 className="w-6 h-6 text-[#e62c86]" />
                            {language === 'fr' ? 'Analyse PCM Complète' : 'Complete PCM Analysis'}
                          </h4>
                          <PCMAnalysisChart assessment={selectedAssessment} language={language} />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                {language === 'fr' ? 'Confirmer la suppression' : 'Confirm deletion'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">
                {language === 'fr' 
                  ? `Êtes-vous sûr de vouloir supprimer l'évaluation de ${showDeleteConfirm.name} ?`
                  : `Are you sure you want to delete the assessment for ${showDeleteConfirm.name}?`
                }
              </p>
              <p className="text-sm text-gray-500">
                {language === 'fr' 
                  ? 'Cette action est irréversible.'
                  : 'This action cannot be undone.'
                }
              </p>
              
              <div className="flex gap-3 justify-end">
                <Button
                  onClick={() => setShowDeleteConfirm(null)}
                  variant="outline"
                  disabled={deleting}
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button
                  onClick={() => handleDeleteSingle(showDeleteConfirm.id, showDeleteConfirm.name)}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? (
                    language === 'fr' ? 'Suppression...' : 'Deleting...'
                  ) : (
                    language === 'fr' ? 'Supprimer' : 'Delete'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#0f1c47]">
                <Settings className="w-5 h-5" />
                {language === 'fr' ? 'Paramètres du Calendrier' : 'Calendar Settings'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* URL du calendrier */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  {language === 'fr' ? 'Lien Google Calendar' : 'Google Calendar Link'}
                </label>
                <Input
                  value={settings.calendar_url}
                  onChange={(e) => setSettings({...settings, calendar_url: e.target.value})}
                  placeholder="https://calendar.app.google/..."
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  {language === 'fr' 
                    ? 'Ce lien sera affiché sur la page de réservation après le questionnaire.'
                    : 'This link will be displayed on the booking page after the questionnaire.'
                  }
                </p>
              </div>
              
              {/* Durée du rendez-vous */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  {language === 'fr' ? 'Durée du rendez-vous' : 'Appointment Duration'}
                </label>
                <div className="flex gap-3">
                  {[15, 30, 45].map((duration) => (
                    <Button
                      key={duration}
                      type="button"
                      onClick={() => setSettings({...settings, calendar_duration: duration})}
                      variant={settings.calendar_duration === duration ? "default" : "outline"}
                      className={settings.calendar_duration === duration 
                        ? "bg-[#e62c86] hover:bg-[#c5246f] text-white flex-1" 
                        : "flex-1"
                      }
                    >
                      {duration} min
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  {language === 'fr' 
                    ? 'Cette durée sera affichée sur la page de réservation (ex: "Offert • 15 minutes").'
                    : 'This duration will be displayed on the booking page (e.g., "Free • 15 minutes").'
                  }
                </p>
              </div>
              
              {/* Boutons d'action */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="outline"
                  disabled={savingSettings}
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button
                  onClick={saveSettings}
                  disabled={savingSettings || !settings.calendar_url}
                  className="bg-[#52a385] hover:bg-[#438f71] text-white"
                >
                  {savingSettings ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {language === 'fr' ? 'Sauvegarde...' : 'Saving...'}
                    </>
                  ) : (
                    language === 'fr' ? 'Sauvegarder' : 'Save'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;